import { useStream } from '@langchain/langgraph-sdk/react';
import { Message } from '@langchain/langgraph-sdk';
import { useCallback, useState, useEffect } from 'react';
import { getApiUrl, getAssistantId, STORAGE_KEYS } from '../utils/config';
import { ChatMessage, ToolCall, Reasoning } from '../types';

interface UseLangGraphOptions {
  persistHistory?: boolean;
  onError?: (error: Error) => void;
}

interface ChatState {
  messages: Message[];
  [key: string]: unknown;
}

/**
 * Hook for integrating with LangGraph
 *
 * This hook provides a simple interface for interacting with a LangGraph agent.
 * It handles message streaming, thread management, and conversation persistence.
 */
export const useLangGraph = (options: UseLangGraphOptions = {}) => {
  const { persistHistory = true, onError } = options;

  // Get the thread ID from localStorage if available
  const [threadId, setThreadId] = useState<string | null>(() => {
    if (persistHistory) {
      return localStorage.getItem(STORAGE_KEYS.THREAD_ID);
    }
    return null;
  });

  // Initialize the LangGraph stream
  const thread = useStream<ChatState>({
    apiUrl: getApiUrl(),
    assistantId: getAssistantId(),
    messagesKey: 'messages',
    threadId,
    onThreadId: (id) => {
      setThreadId(id);
      if (persistHistory && id) {
        localStorage.setItem(STORAGE_KEYS.THREAD_ID, id);
      }
    },
    onError: (error) => {
      console.error('LangGraph error:', error);
      if (onError && error instanceof Error) {
        onError(error);
      }
    },
  });

  // Format messages with additional metadata
  const messages: ChatMessage[] = thread.messages.map((message) => {
    // Convert message content to string if it's not already
    const content = typeof message.content === 'string'
      ? message.content
      : JSON.stringify(message.content);

    // Use type assertion to access LangGraph specific properties
    const langGraphMessage = message as any;

    // Initialize optional properties
    let toolCalls: ToolCall[] | undefined;
    let reasoning: Reasoning | undefined;

    // Process additional_kwargs if they exist
    if (langGraphMessage.additional_kwargs) {
      // Extract tool calls if they exist
      if (langGraphMessage.additional_kwargs.tool_calls &&
          Array.isArray(langGraphMessage.additional_kwargs.tool_calls) &&
          langGraphMessage.additional_kwargs.tool_calls.length > 0) {

        toolCalls = langGraphMessage.additional_kwargs.tool_calls.map((toolCall: any) => ({
          id: toolCall.id || `tool-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
          name: toolCall.function?.name || 'unknown',
          args: toolCall.function?.arguments ?
            (() => {
              try {
                return JSON.parse(toolCall.function.arguments);
              } catch (e) {
                console.warn('Failed to parse tool call arguments:', e);
                return {};
              }
            })() : {}
        }));
      }

      // Extract reasoning from metadata if it exists
      if (langGraphMessage.metadata?.thinking) {
        reasoning = {
          content: langGraphMessage.metadata.thinking,
          duration: langGraphMessage.metadata.thinking_duration || 0
        };
      }
    }

    // Handle tool messages (results of tool calls)
    if (message.type === 'tool' || langGraphMessage.additional_kwargs?.tool_call_id) {
      return {
        id: message.id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
        timestamp: Date.now(),
        type: 'tool',
        content: content,
        additional_kwargs: {
          tool_call_id: langGraphMessage.additional_kwargs?.tool_call_id || langGraphMessage.tool_call_id,
          status: langGraphMessage.status || 'success'
        }
      };
    }

    return {
      id: message.id || `msg-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
      timestamp: Date.now(),
      type: message.type as 'human' | 'ai' | 'system',
      content: content,
      toolCalls,
      reasoning,
      additional_kwargs: langGraphMessage.additional_kwargs
    };
  });

  // Store messages in localStorage if persistHistory is enabled
  useEffect(() => {
    if (persistHistory && messages.length > 0) {
      localStorage.setItem(STORAGE_KEYS.CHAT_HISTORY, JSON.stringify(messages));
    }
  }, [messages, persistHistory]);

  // Send a message to the LangGraph backend
  const sendMessage = useCallback(
    (content: string) => {
      const message: Message = {
        type: 'human',
        content,
      };

      thread.submit(
        { messages: [message] },
        {
          optimisticValues(prev) {
            const prevMessages = prev.messages ?? [];
            return { ...prev, messages: [...prevMessages, message] };
          },
        }
      );
    },
    [thread]
  );

  // Clear the conversation history and start a new chat
  const clearHistory = useCallback(() => {
    if (persistHistory) {
      localStorage.removeItem(STORAGE_KEYS.THREAD_ID);
      localStorage.removeItem(STORAGE_KEYS.CHAT_HISTORY);
    }

    // Reset the thread ID to create a new thread
    setThreadId(null);

    // Force the thread to reset
    // The useStream hook doesn't have a reset method, but we can force it to reset
    // by setting the threadId to null, which will cause it to create a new thread
    // on the next render

    // Create a new thread by forcing a re-initialization of the useStream hook
    // This is done by changing the threadId to null, which causes the hook to create a new thread
    // We don't need to submit any messages - that would be incorrect
  }, [persistHistory]);

  // Stop the current generation
  const stopGeneration = useCallback(() => {
    thread.stop();
  }, [thread]);

  // Regenerate the last AI message
  const regenerateMessage = useCallback(() => {
    if (messages.length > 0) {
      const lastAiMessage = [...messages].reverse().find(msg => msg.type === 'ai');
      if (lastAiMessage) {
        // Find the original message in thread.messages
        const originalMessage = thread.messages.find(msg => msg.id === lastAiMessage.id);
        if (originalMessage) {
          const meta = thread.getMessagesMetadata(originalMessage);
          const parentCheckpoint = meta?.firstSeenState?.parent_checkpoint;

          if (parentCheckpoint) {
            thread.submit(undefined, { checkpoint: parentCheckpoint });
          }
        }
      }
    }
  }, [messages, thread]);

  return {
    messages,
    sendMessage,
    clearHistory,
    stopGeneration,
    regenerateMessage,
    isLoading: thread.isLoading,
    error: thread.error,
    interrupt: thread.interrupt,
    thread, // Expose the thread for direct access
  };
};
